// Enhanced Add Add-on Dialog Component with Advanced Options
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch,
  Slider,
  Badge,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Info as InfoIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  TrendingDown as DiscountIcon,
  TrendingUp as ChargeIcon,
  LocalShipping as DeliveryIcon,
  Restaurant as ServiceIcon,
  CardGiftcard as GiftIcon,
  AccountBalance as TaxIcon,
  Star as PremiumIcon,
  Remove as MinusIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { AnimatePresence } from 'framer-motion';

// Enhanced Add Add-on Dialog with Advanced Configuration
const EnhancedAddAddonDialog = ({ 
  open, 
  onClose, 
  onSave, 
  isMobile 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [newAddOn, setNewAddOn] = useState({
    name: '',
    category: 'charges', // charges, discounts, fees, taxes
    type: 'percentage_add', // percentage_add, percentage_subtract, fixed_add, fixed_subtract
    value: 0,
    enabled: true,
    icon: 'MoneyIcon',
    description: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [previewCalculation, setPreviewCalculation] = useState(null);

  // Enhanced add-on categories with detailed subtypes
  const addOnCategories = {
    charges: {
      label: 'Charges & Fees',
      color: '#667eea',
      icon: <ChargeIcon />,
      description: 'Add extra charges to increase order total',
      types: [
        {
          id: 'percentage_add',
          name: 'Percentage Charge',
          icon: <PercentIcon />,
          description: 'Add percentage-based charge (e.g., 10% service fee)',
          example: 'Service charge, handling fee',
          maxValue: 50,
          suffix: '%',
          color: '#667eea'
        },
        {
          id: 'fixed_add',
          name: 'Fixed Charge',
          icon: <MoneyIcon />,
          description: 'Add fixed amount charge (e.g., $5 delivery fee)',
          example: 'Delivery fee, processing fee',
          maxValue: 9999,
          suffix: '$',
          color: '#10b981'
        }
      ]
    },
    discounts: {
      label: 'Discounts & Savings',
      color: '#ef4444',
      icon: <DiscountIcon />,
      description: 'Apply discounts to reduce order total',
      types: [
        {
          id: 'percentage_subtract',
          name: 'Percentage Discount',
          icon: <PercentIcon />,
          description: 'Apply percentage discount (e.g., 20% off)',
          example: 'Member discount, promotional discount',
          maxValue: 100,
          suffix: '% off',
          color: '#ef4444'
        },
        {
          id: 'fixed_subtract',
          name: 'Fixed Discount',
          icon: <MinusIcon />,
          description: 'Apply fixed amount discount (e.g., $10 off)',
          example: 'Coupon discount, loyalty reward',
          maxValue: 9999,
          suffix: '$ off',
          color: '#f59e0b'
        }
      ]
    },
    taxes: {
      label: 'Taxes & Legal',
      color: '#8b5cf6',
      icon: <TaxIcon />,
      description: 'Government taxes and mandatory charges',
      types: [
        {
          id: 'percentage_add',
          name: 'Tax Rate',
          icon: <TaxIcon />,
          description: 'Apply tax percentage (e.g., 8.5% GST)',
          example: 'GST, VAT, sales tax',
          maxValue: 30,
          suffix: '% tax',
          color: '#8b5cf6'
        }
      ]
    },
    services: {
      label: 'Service Options',
      color: '#ec4899',
      icon: <ServiceIcon />,
      description: 'Optional services and add-ons',
      types: [
        {
          id: 'percentage_add',
          name: 'Service Percentage',
          icon: <ServiceIcon />,
          description: 'Percentage-based service (e.g., 15% tip)',
          example: 'Gratuity, service charge',
          maxValue: 25,
          suffix: '%',
          color: '#ec4899'
        },
        {
          id: 'fixed_add',
          name: 'Service Fee',
          icon: <DeliveryIcon />,
          description: 'Fixed service charge (e.g., $3 packaging)',
          example: 'Packaging, setup fee',
          maxValue: 100,
          suffix: '$',
          color: '#06b6d4'
        }
      ]
    }
  };

  // Enhanced icons with categories
  const enhancedIcons = [
    { id: 'service_charge', name: 'Service Charge', icon: <ServiceIcon />, value: 'ServiceIcon', color: '#667eea', category: 'charges' },
    { id: 'tax', name: 'Tax/GST', icon: <TaxIcon />, value: 'TaxIcon', color: '#8b5cf6', category: 'taxes' },
    { id: 'discount', name: 'Discount', icon: <DiscountIcon />, value: 'DiscountIcon', color: '#ef4444', category: 'discounts' },
    { id: 'delivery', name: 'Delivery Fee', icon: <DeliveryIcon />, value: 'DeliveryIcon', color: '#06b6d4', category: 'services' },
    { id: 'tip', name: 'Tip/Gratuity', icon: <ChargeIcon />, value: 'ChargeIcon', color: '#ec4899', category: 'services' },
    { id: 'gift', name: 'Gift Card', icon: <GiftIcon />, value: 'GiftIcon', color: '#f59e0b', category: 'discounts' },
    { id: 'premium', name: 'Premium Service', icon: <PremiumIcon />, value: 'PremiumIcon', color: '#84cc16', category: 'charges' },
    { id: 'processing', name: 'Processing Fee', icon: <CalculateIcon />, value: 'CalculateIcon', color: '#6366f1', category: 'charges' }
  ];

  // Form validation with enhanced rules
  const validateForm = () => {
    const errors = {};
    
    if (!newAddOn.name.trim()) {
      errors.name = 'Add-on name is required';
    } else if (newAddOn.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (newAddOn.name.length > 50) {
      errors.name = 'Name must be less than 50 characters';
    }
    
    const currentCategory = addOnCategories[newAddOn.category];
    const currentType = currentCategory?.types.find(t => t.id === newAddOn.type);
    const maxValue = currentType?.maxValue || 100;
    
    if (newAddOn.value < 0) {
      errors.value = 'Value cannot be negative';
    } else if (newAddOn.value > maxValue) {
      errors.value = `Value cannot exceed ${maxValue}${currentType?.suffix || ''}`;
    } else if (newAddOn.value === 0) {
      errors.value = 'Value must be greater than 0';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Enhanced preview calculation
  useEffect(() => {
    if (newAddOn.value > 0) {
      const sampleSubtotals = [50, 100, 200];
      const calculations = sampleSubtotals.map(subtotal => {
        let calculatedAmount = 0;
        
        if (newAddOn.type.includes('percentage')) {
          calculatedAmount = subtotal * (newAddOn.value / 100);
        } else {
          calculatedAmount = newAddOn.value;
        }
        
        const finalAmount = newAddOn.type.includes('subtract') ? -calculatedAmount : calculatedAmount;
        const newTotal = Math.max(0, subtotal + finalAmount);
        
        return {
          subtotal,
          addOnAmount: finalAmount,
          total: newTotal,
          applied: true,
          impact: newAddOn.type.includes('subtract') ? 'reduces' : 'increases',
          savings: newAddOn.type.includes('subtract') ? Math.abs(finalAmount) : 0
        };
      });
      
      setPreviewCalculation(calculations);
    } else {
      setPreviewCalculation(null);
    }
  }, [newAddOn.type, newAddOn.value]);

  const steps = [
    {
      label: 'Category',
      description: 'Choose add-on category',
      icon: <InfoIcon />
    },
    {
      label: 'Configuration',
      description: 'Set value and options',
      icon: <PercentIcon />
    },
    {
      label: 'Preview',
      description: 'Review and confirm',
      icon: <PreviewIcon />
    }
  ];

  const getCurrentCategoryInfo = () => {
    return addOnCategories[newAddOn.category] || addOnCategories.charges;
  };

  const getCurrentTypeInfo = () => {
    const category = getCurrentCategoryInfo();
    return category.types.find(t => t.id === newAddOn.type) || category.types[0];
  };

  const handleNext = () => {
    if (currentStep === 0 && !newAddOn.name.trim()) {
      setValidationErrors({ name: 'Name is required to continue' });
      return;
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      // Convert internal type format to simple format for backend
      const saveData = {
        ...newAddOn,
        type: newAddOn.type.includes('percentage') ? 
          (newAddOn.type.includes('subtract') ? 'discount' : 'percentage') :
          (newAddOn.type.includes('subtract') ? 'discount' : 'fixed')
      };
      
      await onSave(saveData);
      handleClose();
    } catch (error) {
      console.error('Error saving add-on:', error);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setNewAddOn({
      name: '',
      category: 'charges',
      type: 'percentage_add',
      value: 0,
      enabled: true,
      icon: 'MoneyIcon',
      description: ''
    });
    setValidationErrors({});
    setPreviewCalculation(null);
    onClose();
  };

  const StepContent = ({ stepIndex, children }) => {
    return (
      <div
        style={{
          display: currentStep === stepIndex ? 'block' : 'none',
          opacity: currentStep === stepIndex ? 1 : 0,
          transform: currentStep === stepIndex ? 'translateX(0)' : 'translateX(20px)',
          transition: 'all 0.3s ease'
        }}
      >
        {children}
      </div>
    );
  };

  const categoryInfo = getCurrentCategoryInfo();
  const typeInfo = getCurrentTypeInfo();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          borderRadius: isMobile ? 0 : 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          overflow: 'hidden',
          minHeight: isMobile ? '100vh' : '85vh',
          maxHeight: '95vh'
        }
      }}
    >
      {/* Enhanced Header */}
      <DialogTitle sx={{ 
        background: `linear-gradient(135deg, ${categoryInfo.color} 0%, ${categoryInfo.color}dd 100%)`,
        color: 'white',
        p: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              width: 56, 
              height: 56,
              backdropFilter: 'blur(10px)'
            }}>
              {categoryInfo.icon}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Create New Add-on
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {categoryInfo.description}
              </Typography>
              <Chip 
                label={categoryInfo.label}
                size="small"
                sx={{ 
                  mt: 1,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        {/* Progress Stepper */}
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Stepper activeStep={currentStep} orientation={isMobile ? 'vertical' : 'horizontal'}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel 
                  icon={
                    <Avatar sx={{ 
                      width: 40, 
                      height: 40, 
                      bgcolor: index <= currentStep ? categoryInfo.color : 'grey.300',
                      color: 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      {index < currentStep ? <CheckIcon /> : step.icon}
                    </Avatar>
                  }
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ p: 4, minHeight: '400px', maxHeight: '60vh', overflow: 'auto' }}>
          <AnimatePresence mode="wait">
            {/* Step 1: Category Selection */}
            {currentStep === 0 && (
              <StepContent stepIndex={0}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: categoryInfo.color }}>
                  📝 Basic Information
                </Typography>

                <TextField
                  fullWidth
                  label="Add-on Name"
                  value={newAddOn.name}
                  onChange={(e) => setNewAddOn(prev => ({ ...prev, name: e.target.value }))}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name || 'Enter a descriptive name (e.g., "Service Charge", "Member Discount")'}
                  sx={{ mb: 4 }}
                  autoFocus
                />

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  🏷️ Select Category:
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {Object.entries(addOnCategories).map(([key, category]) => {
                    const isSelected = newAddOn.category === key;
                    
                    return (
                      <Grid item xs={12} sm={6} md={3} key={key}>
                        <Card
                          onClick={() => {
                            setNewAddOn(prev => ({ 
                              ...prev, 
                              category: key,
                              type: category.types[0].id,
                              icon: enhancedIcons.find(i => i.category === key)?.value || 'MoneyIcon'
                            }));
                          }}
                          sx={{
                            cursor: 'pointer',
                            border: isSelected ? `3px solid ${category.color}` : '3px solid transparent',
                            bgcolor: isSelected ? `${category.color}08` : 'white',
                            transition: 'all 0.3s ease',
                            height: '100%',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 12px 32px ${category.color}25`
                            }
                          }}
                        >
                          <CardContent sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                            <Avatar sx={{ 
                              bgcolor: `${category.color}15`, 
                              color: category.color,
                              mx: 'auto',
                              mb: 2,
                              width: 56,
                              height: 56
                            }}>
                              {category.icon}
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: category.color }}>
                              {category.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                              {category.description}
                            </Typography>
                            {isSelected && (
                              <Chip 
                                label="Selected"
                                size="small"
                                sx={{ 
                                  mt: 2,
                                  bgcolor: category.color,
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  ⚙️ Select Type:
                </Typography>

                <Grid container spacing={2}>
                  {categoryInfo.types.map((type) => {
                    const isSelected = newAddOn.type === type.id;
                    
                    return (
                      <Grid item xs={12} sm={6} key={type.id}>
                        <Card
                          onClick={() => setNewAddOn(prev => ({ ...prev, type: type.id }))}
                          sx={{
                            cursor: 'pointer',
                            border: isSelected ? `2px solid ${type.color}` : '2px solid #e2e8f0',
                            bgcolor: isSelected ? `${type.color}08` : 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 24px ${type.color}25`
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar sx={{ 
                                bgcolor: `${type.color}15`, 
                                color: type.color,
                                mr: 2,
                                width: 48,
                                height: 48
                              }}>
                                {type.icon}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: type.color }}>
                                  {type.name}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  bgcolor: `${type.color}15`,
                                  color: type.color,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontWeight: 600
                                }}>
                                  Max: {type.maxValue}{type.suffix}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {type.description}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: type.color,
                              fontWeight: 600,
                              fontStyle: 'italic'
                            }}>
                              Examples: {type.example}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </StepContent>
            )}

            {/* Step 2: Configuration */}
            {currentStep === 1 && (
              <StepContent stepIndex={1}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: typeInfo.color }}>
                  ⚙️ Configuration Settings
                </Typography>

                <Alert 
                  severity="info" 
                  sx={{ mb: 4, borderRadius: 2 }}
                  icon={typeInfo.icon}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {typeInfo.description}
                  </Typography>
                  <Typography variant="body2">
                    Example: {typeInfo.example}
                  </Typography>
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      💰 Value Settings
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label={`${typeInfo.name} Value`}
                      type="number"
                      value={newAddOn.value}
                      onChange={(e) => setNewAddOn(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                      error={!!validationErrors.value}
                      helperText={validationErrors.value || `Enter value (0 - ${typeInfo.maxValue})`}
                      InputProps={{
                        startAdornment: React.cloneElement(typeInfo.icon, { sx: { mr: 1, color: typeInfo.color } }),
                        endAdornment: <Typography variant="body2" color="text.secondary">{typeInfo.suffix}</Typography>
                      }}
                      inputProps={{
                        min: 0,
                        max: typeInfo.maxValue,
                        step: newAddOn.type.includes('percentage') ? 0.1 : 0.01
                      }}
                      sx={{ mb: 2 }}
                    />

                    <Box sx={{ px: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Quick Values:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {newAddOn.type.includes('percentage') ? 
                          [5, 10, 15, 20, 25].map(val => (
                            <Chip
                              key={val}
                              label={`${val}%`}
                              size="small"
                              onClick={() => setNewAddOn(prev => ({ ...prev, value: val }))}
                              sx={{ 
                                bgcolor: newAddOn.value === val ? typeInfo.color : 'grey.100',
                                color: newAddOn.value === val ? 'white' : 'text.primary'
                              }}
                            />
                          )) :
                          [1, 2, 5, 10, 15].map(val => (
                            <Chip
                              key={val}
                              label={`$${val}`}
                              size="small"
                              onClick={() => setNewAddOn(prev => ({ ...prev, value: val }))}
                              sx={{ 
                                bgcolor: newAddOn.value === val ? typeInfo.color : 'grey.100',
                                color: newAddOn.value === val ? 'white' : 'text.primary'
                              }}
                            />
                          ))
                        }
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      🎨 Visual Settings
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                      Choose Icon:
                    </Typography>

                    <Grid container spacing={1} sx={{ mb: 3 }}>
                      {enhancedIcons.filter(icon => icon.category === newAddOn.category || icon.category === 'all').map((iconOption) => (
                        <Grid item xs={3} key={iconOption.value}>
                          <Tooltip title={iconOption.name}>
                            <Card
                              onClick={() => setNewAddOn(prev => ({ ...prev, icon: iconOption.value }))}
                              sx={{
                                cursor: 'pointer',
                                border: newAddOn.icon === iconOption.value ? `2px solid ${iconOption.color}` : '2px solid transparent',
                                bgcolor: newAddOn.icon === iconOption.value ? `${iconOption.color}08` : 'white',
                                transition: 'all 0.2s ease',
                                '&:hover': { transform: 'scale(1.05)' }
                              }}
                            >
                              <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                                <Avatar sx={{ 
                                  width: 40, 
                                  height: 40, 
                                  bgcolor: `${iconOption.color}15`,
                                  mx: 'auto'
                                }}>
                                  {React.cloneElement(iconOption.icon, { 
                                    sx: { fontSize: 20, color: iconOption.color } 
                                  })}
                                </Avatar>
                              </CardContent>
                            </Card>
                          </Tooltip>
                        </Grid>
                      ))}
                    </Grid>

                    <TextField
                      fullWidth
                      label="Description (Optional)"
                      value={newAddOn.description}
                      onChange={(e) => setNewAddOn(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter a detailed description..."
                      multiline
                      rows={3}
                      helperText="This will help customers understand the add-on"
                    />
                  </Grid>
                </Grid>
              </StepContent>
            )}

            {/* Step 3: Preview */}
            {currentStep === 2 && (
              <StepContent stepIndex={2}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: typeInfo.color }}>
                  👀 Preview & Confirmation
                </Typography>

                {/* Add-on Summary */}
                <Paper sx={{ p: 4, mb: 4, border: `2px solid ${typeInfo.color}30`, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                    <Avatar sx={{ 
                      bgcolor: `${typeInfo.color}15`, 
                      color: typeInfo.color,
                      width: 72,
                      height: 72
                    }}>
                      {enhancedIcons.find(i => i.value === newAddOn.icon)?.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {newAddOn.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip 
                          label={categoryInfo.label}
                          size="small"
                          sx={{ bgcolor: `${categoryInfo.color}15`, color: categoryInfo.color, fontWeight: 600 }}
                        />
                        <Chip 
                          label={`${typeInfo.name} • ${newAddOn.value}${typeInfo.suffix}`}
                          size="small"
                          sx={{ bgcolor: `${typeInfo.color}15`, color: typeInfo.color, fontWeight: 600 }}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {newAddOn.description || typeInfo.description}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Conditions Display - Simplified */}
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">STATUS</Typography>
                      <Typography variant="h6" color={typeInfo.color}>
                        Active
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">CATEGORY</Typography>
                      <Typography variant="h6" color={typeInfo.color}>
                        {categoryInfo.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">TYPE</Typography>
                      <Typography variant="h6" color={typeInfo.color}>
                        {typeInfo.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">VALUE</Typography>
                      <Typography variant="h6" color={typeInfo.color}>
                        {newAddOn.value}{typeInfo.suffix}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Enhanced Calculation Preview */}
                {previewCalculation && (
                  <Paper sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                      <CalculateIcon sx={{ mr: 1, color: typeInfo.color }} />
                      💰 Calculation Preview
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {previewCalculation.map((calc, index) => (
                        <Grid item xs={12} sm={4} key={index}>
                          <Card sx={{ 
                            p: 2, 
                            bgcolor: 'white',
                            border: `1px solid ${typeInfo.color}30`
                          }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              ${calc.subtotal} Order
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">Subtotal:</Typography>
                              <Typography variant="body2">${calc.subtotal.toFixed(2)}</Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color={calc.addOnAmount < 0 ? 'error' : 'primary'}>
                                {newAddOn.name}:
                              </Typography>
                              <Typography variant="body2" color={calc.addOnAmount < 0 ? 'error' : 'primary'}>
                                {calc.addOnAmount < 0 ? '-' : '+'}${Math.abs(calc.addOnAmount).toFixed(2)}
                              </Typography>
                            </Box>
                            
                            <Divider sx={{ my: 1 }} />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Total:
                              </Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: typeInfo.color }}>
                                ${calc.total.toFixed(2)}
                              </Typography>
                            </Box>

                            {calc.savings > 0 && (
                              <Chip 
                                label={`Saves ${calc.savings.toFixed(2)}`}
                                size="small"
                                sx={{ bgcolor: 'success.light', color: 'success.dark' }}
                              />
                            )}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    <Alert 
                      severity={newAddOn.type.includes('subtract') ? 'success' : 'info'} 
                      sx={{ mt: 3, borderRadius: 2 }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        📊 Summary:
                      </Typography>
                      <Typography variant="body2">
                        This add-on will {newAddOn.type.includes('subtract') ? 'reduce' : 'increase'} qualifying orders by{' '}
                        {newAddOn.type.includes('percentage') ? 
                          `${newAddOn.value}% of the order total` : 
                          `$${newAddOn.value.toFixed(2)} per order`
                        }.
                      </Typography>
                    </Alert>
                  </Paper>
                )}
              </StepContent>
            )}
          </AnimatePresence>
        </Box>
      </DialogContent>

      {/* Enhanced Footer */}
      <DialogActions sx={{ 
        p: 3, 
        bgcolor: '#f8fafc', 
        gap: 2,
        borderTop: '1px solid #e2e8f0'
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        
        {currentStep > 0 && (
          <Button 
            onClick={handleBack} 
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Back
          </Button>
        )}
        
        <Box sx={{ flex: 1 }} />
        
        {currentStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={currentStep === 0 && !newAddOn.name.trim()}
            sx={{ 
              background: `linear-gradient(135deg, ${categoryInfo.color} 0%, ${categoryInfo.color}dd 100%)`,
              minWidth: 120,
              fontWeight: 600
            }}
          >
            Next Step
          </Button>
        ) : (
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={!newAddOn.name.trim() || newAddOn.value <= 0}
            startIcon={<CheckIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              minWidth: 150,
              fontWeight: 600
            }}
          >
            Create Add-on
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedAddAddonDialog;